'use client';

import { devError, devLog } from '@/lib/utils/devLog';

import type { Watch } from './types';

/** Warranty lookup outcome from the service identify BFF. */
export interface ServiceIdentifyWatchResult {
  code: number;
  data?: Watch;
}

interface ServiceIdentifyWatchParams {
  sn: string;
  locale?: string;
}

export function buildServiceIdentifyWatchUrl(params: ServiceIdentifyWatchParams): string {
  const query = new URLSearchParams({ sn: params.sn.trim() });
  if (params.locale) query.set('locale', params.locale);
  return `/api/service/identify-watch?${query.toString()}`;
}

/**
 * SN-only warranty lookup for service flows.
 *
 * Returns null only on transport failure; backend outcomes come back as `{ code, data }`.
 */
export async function fetchServiceIdentifyWatch(
  params: ServiceIdentifyWatchParams
): Promise<ServiceIdentifyWatchResult | null> {
  try {
    const response = await fetch(buildServiceIdentifyWatchUrl(params));

    if (!response.ok) {
      devError('[MyWatches Client] Service identify failed:', response.status);
      return null;
    }

    const json = await response.json();

    if (!json.success || typeof json.data?.code !== 'number') {
      devError('[MyWatches Client] Service identify returned no code');
      return null;
    }

    devLog('[MyWatches Client] Service identify:', json.data.code);

    return { code: json.data.code, data: json.data.data };
  } catch (error) {
    devError('[MyWatches Client] Service identify error:', error);
    return null;
  }
}
